import React, { useEffect, useRef } from 'react';

type Burst = {
  x: string;
  y: string;
  delay: string;
  dur: string;
  hue: 'gold' | 'pink' | 'blue';
};

// Staggered so the sky is always gently alive but never busy.
const BURSTS: Burst[] = [
  { x: '14%', y: '38%', delay: '0.2s', dur: '6.4s', hue: 'gold' },
  { x: '72%', y: '28%', delay: '1.8s', dur: '6.2s', hue: 'pink' },
  { x: '88%', y: '62%', delay: '3.4s', dur: '6.8s', hue: 'blue' },
  { x: '44%', y: '24%', delay: '2.6s', dur: '7.0s', hue: 'pink' },
  { x: '60%', y: '70%', delay: '4.2s', dur: '6.0s', hue: 'gold' },
  { x: '28%', y: '72%', delay: '5.0s', dur: '7.0s', hue: 'blue' },
];

/**
 * The fairy's flight path across the hero: [x%, y%, time as % of the loop].
 * Must match the `mwPixieX` / `mwPixieY` keyframes in lounge-modern.css.
 * The flight takes ~6s of a 16s loop: slow and graceful.
 */
export const PIXIE_PATH: Array<[number, number, number]> = [
  [-8, 72, 0],
  [22, 34, 10],
  [48, 52, 19],
  [74, 26, 28],
  [104, 40, 38],
];
export const PIXIE_LOOP_SECONDS = 16;
export const PIXIE_START_SECONDS = 1.5;

/** Where (and when) the fairy is at horizontal position `x` (in %). */
export const pixiePointAt = (x: number) => {
  for (let i = 1; i < PIXIE_PATH.length; i += 1) {
    const [x0, y0, t0] = PIXIE_PATH[i - 1];
    const [x1, y1, t1] = PIXIE_PATH[i];
    if (x <= x1) {
      const f = (x - x0) / (x1 - x0);
      return { y: y0 + (y1 - y0) * f, t: t0 + (t1 - t0) * f };
    }
  }
  const [, y, t] = PIXIE_PATH[PIXIE_PATH.length - 1];
  return { y, t };
};

// Deterministic pseudo-random numbers so the glitter pattern is stable
// between renders (and in tests).
const seeded = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Pixie dust: fine glitter (1-2px specks) released right behind the fairy.
// Each speck appears as she passes its spot, then glints, drifts down and
// fades, so her path leaves a soft falling curtain of sparkle.
export const PIXIE_DUST_COUNT = 72;
const random = seeded(1955);
const DUST = Array.from({ length: PIXIE_DUST_COUNT }, (_, i) => {
  const x = 1 + (i / (PIXIE_DUST_COUNT - 1)) * 98 + (random() - 0.5) * 2;
  const { y, t } = pixiePointAt(x);
  const lag = random() * 0.35;
  const roll = random();
  return {
    x: `${x.toFixed(2)}%`,
    y: `${(y + (random() - 0.5) * 14).toFixed(2)}%`,
    delay: `${(PIXIE_START_SECONDS + (t / 100) * PIXIE_LOOP_SECONDS + lag).toFixed(2)}s`,
    size: roll > 0.9 ? 2 : roll > 0.45 ? 1.5 : 1,
    fall: `${Math.round(16 + random() * 30)}px`,
    drift: `${Math.round((random() - 0.5) * 14)}px`,
    hue: roll > 0.93 ? 'pink' : roll < 0.07 ? 'blue' : 'gold',
  };
});

/** Set on the `.mw-hero` while it is off screen; pauses its animations. */
export const HERO_FX_PAUSED_CLASS = 'mw-hero--fx-paused';

/**
 * Decorative night-sky layer for the Lounge hero: soft glow, twinkling
 * stars, fireworks that rise before they bloom, and a fairy light that
 * flies across leaving pixie dust in front of the fireworks. Purely visual
 * (aria-hidden); all motion stops under prefers-reduced-motion
 * (see lounge-modern.css). The show is finite and pauses while the hero is
 * scrolled out of view, so it costs nothing while members read the feed.
 */
export const LoungeHeroFx: React.FC = () => {
  const glowRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const hero = glowRef.current?.parentElement;
    if (!hero || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      hero.classList.toggle(HERO_FX_PAUSED_CLASS, !entry.isIntersecting);
    });
    observer.observe(hero);
    return () => {
      observer.disconnect();
      hero.classList.remove(HERO_FX_PAUSED_CLASS);
    };
  }, []);

  return (
  <>
    <span ref={glowRef} className='mw-hero-glow' aria-hidden='true' />
    <span className='mw-hero-stars' aria-hidden='true' />
    <span className='mw-hero-stars mw-hero-stars--late' aria-hidden='true' />
    <span className='mw-hero-fireworks' aria-hidden='true'>
      {BURSTS.map((burst) => (
        <span
          key={`${burst.x}-${burst.y}`}
          className={`mw-hero-burst mw-hero-burst--${burst.hue}`}
          style={
            {
              '--x': burst.x,
              '--y': burst.y,
              '--delay': burst.delay,
              '--dur': burst.dur,
            } as React.CSSProperties
          }
        />
      ))}
    </span>
    <span className='mw-hero-pixie-layer' aria-hidden='true'>
      {DUST.map((dust, index) => (
        <span
          key={`dust-${index}`}
          className={`mw-hero-dust mw-hero-dust--${dust.hue}`}
          style={
            {
              '--x': dust.x,
              '--y': dust.y,
              '--delay': dust.delay,
              '--size': `${dust.size}px`,
              '--fall': dust.fall,
              '--drift': dust.drift,
            } as React.CSSProperties
          }
        />
      ))}
      <span className='mw-hero-pixie' />
    </span>
  </>
  );
};
